import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {useEffect} from "react";
import {FormattedMessage} from "react-intl";
import {useDebounce} from "use-debounce";
import type {Location, LocationSuggestion} from "@/api/apiClient";
import {getLocationSuggestions} from "@/api/apiClient";
import {ScrollFadeWrapper} from "@/components/ScrollFadeWrapper";
import {usePublicOrderHash} from "@/features/configuration";
import {generateUUID} from "@/utils/uuid";
import {LocationSuggestionItem} from "./components/LocationSuggestionItem";
import {LocationSuggestionItemSkeleton} from "./components/LocationSuggestionItemSkeleton";
import styles from "./LocationSuggestions.module.css";
import {getFormattedPredefinedSuggestions, transformAddressType} from "./utils/location";

type LocationSuggestionsProps = {
    query: string;
    type: "pickup" | "dropoff";
    limit?: number;
    onSelectLocation: (suggestion: LocationSuggestion, suggestSessionId?: string) => void;
    queryParams?: {
        pickup?: Location;
        near?: Location;
    };
    firstSuggestionRef?: {
        current: {
            suggestion: LocationSuggestion;
            sessionId?: string;
        } | null;
    };
};

export function LocationSuggestions({
    query,
    type,
    limit = 20,
    onSelectLocation,
    queryParams,
    firstSuggestionRef,
}: LocationSuggestionsProps) {
    const [debouncedQuery] = useDebounce(query, 300);
    const {orderHash, isInitialized} = usePublicOrderHash();

    const {
        data: suggestionsData,
        isFetching,
        isError,
    } = useQuery({
        queryKey: [
            "suggestions",
            type,
            debouncedQuery,
            queryParams?.near,
            queryParams?.pickup,
            orderHash,
        ],
        queryFn: async () => {
            const sessionId = generateUUID();

            // For empty query (predefined places)
            if (debouncedQuery === "") {
                const params = {
                    serviceType: "TAXI",
                    query: debouncedQuery,
                    addressType: transformAddressType(type),
                    suggestSessionId: sessionId,
                    near: queryParams?.near,
                    pickup: queryParams?.pickup,
                    publicHash: orderHash ?? undefined,
                } as const;
                const result = await getLocationSuggestions(params);
                return {sessionId, data: result};
            }

            // For search query, send full params
            const params = {
                serviceType: "TAXI",
                query: debouncedQuery,
                addressType: transformAddressType(type),
                suggestSessionId: sessionId,
                ...queryParams,
                publicHash: orderHash ?? undefined,
            } as const;
            const result = await getLocationSuggestions(params);
            return {sessionId, data: result};
        },
        select: (response) => {
            const suggestions =
                debouncedQuery === ""
                    ? getFormattedPredefinedSuggestions(response.data.suggestions).slice(0, limit)
                    : response.data.suggestions.slice(0, limit);

            return {
                sessionId: response.sessionId,
                suggestions,
            };
        },
        enabled: isInitialized,
        placeholderData: keepPreviousData,
    });

    const suggestions = suggestionsData?.suggestions;

    const showSkeletons = isFetching && !suggestions;

    // Update first suggestion ref
    useEffect(() => {
        if (!firstSuggestionRef) return;

        const firstSuggestion = suggestions?.[0];
        if (firstSuggestion) {
            firstSuggestionRef.current = {
                suggestion: firstSuggestion,
                sessionId: suggestionsData?.sessionId,
            };
        } else {
            firstSuggestionRef.current = null;
        }
    }, [suggestions, suggestionsData?.sessionId, firstSuggestionRef]);

    return (
        <ScrollFadeWrapper>
            {showSkeletons ? (
                <div className={styles.suggestions}>
                    <LocationSuggestionItemSkeleton />
                </div>
            ) : suggestions && suggestions?.length > 0 ? (
                <div className={styles.suggestions}>
                    {suggestions.map((suggestion, index) => (
                        <LocationSuggestionItem
                            key={suggestion.location.id ?? index}
                            onClick={() => {
                                onSelectLocation(suggestion, suggestionsData?.sessionId);
                            }}
                            suggestion={suggestion}
                        />
                    ))}
                </div>
            ) : isError ? (
                <div className={styles.emptyState}>
                    <FormattedMessage
                        id="generic_error"
                        defaultMessage="Something went wrong. Please try again later."
                    />
                </div>
            ) : debouncedQuery === "" ? (
                <div className={styles.emptyState}>
                    <FormattedMessage
                        id="search_for_destination"
                        defaultMessage="Search for the destination or choose on map"
                    />
                </div>
            ) : (
                <div className={styles.emptyState}>
                    <FormattedMessage
                        id="no_destination_found"
                        defaultMessage="No destination found"
                        values={{search: debouncedQuery}}
                    />
                </div>
            )}
        </ScrollFadeWrapper>
    );
}
